import ModelsAPI from './models';
import {
  UNKNOWN_VIDEO_CAPABILITIES,
  VideoCapabilities,
  parseVideoCapabilities,
} from './videoCapabilities';

/**
 * What the registry said about a video model, and WHETHER IT SAID IT.
 *
 * The two halves are separate because the all-off capability shape is
 * ambiguous on its own: it is what a model with nothing declared parses to AND
 * what an unreachable registry has to fall back to. For RENDERING that
 * ambiguity is harmless — both mean "offer no controls" — but for anything that
 * WRITES, the two are opposites. `known: false` means "do not act on this".
 */
export interface VideoCapabilitiesResult {
  caps: VideoCapabilities;
  /** True only when the registry actually answered for this model id. */
  known: boolean;
}

const UNKNOWN_RESULT: VideoCapabilitiesResult = {
  caps: UNKNOWN_VIDEO_CAPABILITIES,
  known: false,
};

const warnedModels = new Set<string>();

const unknownFor = (modelId: string): VideoCapabilitiesResult => {
  if (!warnedModels.has(modelId)) {
    warnedModels.add(modelId);
    console.warn(`Video capabilities unavailable for "${modelId}" — treating them as unknown.`);
  }
  return UNKNOWN_RESULT;
};

/**
 * One video model's capabilities, fetched and parsed — the way every surface
 * asks that question, with one exception noted at the bottom.
 *
 * `getModelConstraints(id, 'video')` followed by
 * `parseVideoCapabilities(c.capabilities || {})` used to be written out at five
 * call sites with FOUR different failure policies (unknown-and-reset,
 * known-but-all-off, null, swallow-and-keep-stale). That is how two surfaces
 * end up disagreeing about the same model, so the pair lives here once and the
 * failure policy is stated once:
 *
 *   **a registry failure yields
 *   `{ caps: UNKNOWN_VIDEO_CAPABILITIES, known: false }`.** The caps half is the
 *   all-off, all-default shape, which every consumer already renders as
 *   "nothing is declared": no reference rows, no chip, no frame slots. Offering
 *   nothing is the only honest thing a UI can do with an unreachable registry —
 *   inventing caps would submit a request the provider refuses. The `known`
 *   half is what stops that from ALSO reading as a positive statement: a caller
 *   that DROPS the operator's stored reference lists for a kind "this model
 *   does not declare" must not do so because the registry was down, so it gates
 *   on `known`.
 *
 * NEVER REJECTS, and never throws synchronously either — every caller is a
 * render effect, where a rejection is an unhandled promise rather than a
 * fallback. The `try` covers a synchronous throw out of `getModelConstraints`
 * (a stubbed `axios.get` returning a non-promise, an interceptor throwing),
 * which the promise-chain form would otherwise let escape.
 *
 * The failure is logged HERE and nowhere else, once per model id: a cold canvas
 * with N nodes on one broken model would otherwise print N identical lines.
 *
 * A plain `.then` rather than async/await, for the same reason
 * `fetchModelConstraints` is: every caller settles a `useState` off this, and
 * each extra `await` is one more microtask before that state lands.
 *
 * THE ONE SURFACE THAT DOES NOT USE THIS: `CheckpointProviderSelector` asks the
 * same question per candidate model with the OPPOSITE failure policy — unknown
 * must not read as incompatible, or a registry hiccup silently empties the
 * model picker. Do not "finish" the DRY pass by migrating it.
 */
export function fetchVideoCapabilities(modelId: string): Promise<VideoCapabilitiesResult> {
  // No model chosen yet is not a registry failure and warrants no warning — but
  // it is not a declaration either, so `known` stays false.
  if (!modelId) return Promise.resolve(UNKNOWN_RESULT);
  try {
    return ModelsAPI.getModelConstraints(modelId, 'video').then(
      (constraints) => ({
        caps: parseVideoCapabilities(constraints.capabilities || {}),
        known: true,
      }),
      () => unknownFor(modelId)
    );
  } catch {
    return Promise.resolve(unknownFor(modelId));
  }
}

/** Test seam: forget which models have already been warned about. */
export const resetVideoCapabilityWarnings = (): void => warnedModels.clear();
